/**
 * Layout.jsx - Main application layout.
 *
 * Sidebar is an overlay that slides in from the left. It never pushes
 * or shrinks the main content area. A hamburger toggle is always visible
 * so the user can open/close the nav on any screen size.
 *
 * This component wraps all authenticated routes.
 */

import { useState, useEffect, useCallback } from "react";
import { Outlet } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import CompanySelector from "./CompanySelector";
import { useAuth } from "../auth/useAuth";

/* ------------------------------------------------------------------ */
/* Dark mode helper                                                    */
/* ------------------------------------------------------------------ */

function getInitialDarkMode() {
  try {
    const stored = localStorage.getItem("vms-dark-mode");
    if (stored !== null) return stored === "true";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function Layout({
  title,
  subtitle,
  period,
  onPeriod,
  onRefresh,
  refreshing,
  children,
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(getInitialDarkMode);
  const { user, logout } = useAuth();

  /* Apply dark class to <html> */
  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    try {
      localStorage.setItem("vms-dark-mode", String(darkMode));
    } catch {
      /* ignore */
    }
  }, [darkMode]);

  const toggleDarkMode = useCallback(() => {
    setDarkMode((d) => !d);
  }, []);

  const toggleSidebar = useCallback(() => setSidebarOpen((o) => !o), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  return (
    <div style={styles.root}>
      {/* Sidebar overlay */}
      <Sidebar
        open={sidebarOpen}
        onClose={closeSidebar}
        darkMode={darkMode}
        onToggleTheme={toggleDarkMode}
        user={user}
        onSignOut={logout}
      />

      {/* Main area — always full width, sidebar overlays on top */}
      <div style={styles.main}>
        {/* Topbar row with hamburger toggle */}
        <div style={styles.topbarRow}>
          <button
            className="btn ghost"
            onClick={toggleSidebar}
            aria-label={sidebarOpen ? "Close navigation" : "Open navigation"}
            style={styles.menuBtn}
          >
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <Topbar
            title={title}
            subtitle={subtitle}
            period={period}
            onPeriod={onPeriod}
            onRefresh={onRefresh}
            refreshing={refreshing}
          />
          <div style={styles.companySlot}>
            <CompanySelector />
          </div>
        </div>

        {/* Page content */}
        <main style={styles.content}>
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const styles = {
  root: {
    display: "flex",
    minHeight: "100vh",
    background: "var(--bg)",
    position: "relative",
  },
  main: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  topbarRow: {
    display: "flex",
    alignItems: "stretch",
    position: "relative",
  },
  companySlot: {
    position: "absolute",
    right: 24,
    top: 14,
    zIndex: 60,
  },
  menuBtn: {
    flexShrink: 0,
    borderRight: "1px solid var(--line)",
    borderRadius: 0,
    padding: "7px 12px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    flex: 1,
    padding: 24,
    overflowY: "auto",
    overflowX: "auto",
  },
};
